import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useAuth } from "../../../auth/authContext";
import { getFacilitatorCapabilityDecisions } from "../services/facilitatorCapabilityApi";

const FacilitatorCapabilityContext = createContext(null);
const INITIAL_STATE = Object.freeze({ status: "idle", decisions: null, error: null });

export function FacilitatorCapabilityProvider({ children }) {
  const { user, loadingUser } = useAuth();
  const [state, setState] = useState(INITIAL_STATE);
  const requestRef = useRef(null);

  const refresh = useCallback(async () => {
    if (!user) {
      setState(INITIAL_STATE);
      return null;
    }
    if (requestRef.current) return requestRef.current;
    setState((current) => ({ ...current, status: "loading", error: null }));
    const pending = getFacilitatorCapabilityDecisions()
      .then((decisions) => {
        setState({ status: "ready", decisions, error: null });
        return decisions;
      })
      .catch((error) => {
        setState({ status: "unavailable", decisions: null, error });
        return null;
      })
      .finally(() => { requestRef.current = null; });
    requestRef.current = pending;
    return pending;
  }, [user]);

  useEffect(() => {
    if (!loadingUser) refresh();
  }, [loadingUser, refresh]);

  const value = useMemo(() => {
    const activityDecision = state.decisions?.activityAuthor || null;
    const assignmentDecision = state.decisions?.assignmentManage || null;
    return {
      ...state,
      activityAuthorDecision: activityDecision,
      assignmentManageDecision: assignmentDecision,
      canAuthorActivities: activityDecision?.effect === "allow",
      canManageAssignments: assignmentDecision?.effect === "allow",
      refresh,
    };
  }, [refresh, state]);

  return (
    <FacilitatorCapabilityContext.Provider value={value}>
      {children}
    </FacilitatorCapabilityContext.Provider>
  );
}

export function useFacilitatorCapabilities() {
  const value = useContext(FacilitatorCapabilityContext);
  if (!value) throw new Error("FacilitatorCapabilityProvider is required");
  return value;
}
